import {Component, input, output, signal} from '@angular/core';
import {BytesConverterPipe} from '../../pipes/bytes-converter.pipe';

// ─── Types ───────────────────────────────────────────────────────────────────

type HighlightedChunk = { datasource: number; chunk: number };

export type CitationChunk = {
    index: number;
    content: string;
    /** Página inicial do trecho (opcional; presente só em PDFs, por chunk). */
    pageStart?: number;
    /** Página final do trecho (opcional; presente só em PDFs, por chunk). */
    pageEnd?: number;
};

export type Citation = {
    index: number;
    uri: string;
    title?: string;
    filename: string;
    contentType: string;
    fileSize: string;
    createdAt: string;
    lastModified: string;
    chunks: CitationChunk[];
};

// ─── Events ──────────────────────────────────────────────────────────────────

export type CitationClickedEvent = {
    citation: Citation;
};

export type CitationChunkClickedEvent = {
    citation: Citation;
    chunk: CitationChunk;
};

// ─── Component ───────────────────────────────────────────────────────────────

@Component({
    selector: 'ai-citations',
    standalone: true,
    imports: [BytesConverterPipe],
    templateUrl: './citations.component.html',
    styleUrls: ['./citations.component.css'],
})
export class CitationsComponent {
    /** Lista de citações a serem exibidas. */
    readonly citations = input<Citation[]>([]);

    /** Emitido ao clicar no nome/título de uma citação. */
    readonly citationClicked = output<CitationClickedEvent>();

    /** Emitido ao clicar em um chunk de uma citação. */
    readonly citationChunkClicked = output<CitationChunkClickedEvent>();

    /**
     * Índices das citações cujas listas de chunks estão visíveis.
     * Controla apenas a visibilidade da lista — independente de qual chunk está aberto.
     */
    protected readonly openCitations = signal<Set<number>>(new Set());

    /**
     * Chave do chunk atualmente expandido no formato `"citationIndex-chunkIndex"`.
     * Apenas um chunk pode estar aberto por vez. Fechar o último chunk não esconde
     * a lista — para isso é necessário clicar no toggle da citação-pai.
     */
    protected readonly openChunkKey = signal<string | null>(null);

    /** Chunk atualmente destacado pela animação de highlight. */
    protected readonly highlightedChunk = signal<HighlightedChunk | null>(null);

    /** Retorna a chave única de um chunk. */
    protected chunkKey(citationIndex: number, chunkIndex: number): string {
        return `${citationIndex}-${chunkIndex}`;
    }

    /** Verifica se a lista de chunks de uma citação está visível. */
    protected isChunksOpen(citation: Citation): boolean {
        return this.openCitations().has(citation.index);
    }

    /**
     * Indica se o chunk possui informação de página (opcional; só em PDFs).
     */
    protected chunkHasPage(chunk: CitationChunk): boolean {
        return chunk.pageStart != null || chunk.pageEnd != null;
    }

    /**
     * Rótulo de página do chunk: `Página N` quando início e fim coincidem (ou
     * só um está presente); `Páginas N-M` quando formam um intervalo. Requer
     * {@link chunkHasPage}.
     */
    protected chunkPageLabel(chunk: CitationChunk): string {
        const start = chunk.pageStart ?? chunk.pageEnd;
        const end = chunk.pageEnd ?? chunk.pageStart;
        return start === end ? `Página ${start}` : `Páginas ${start}-${end}`;
    }

    /** Verifica se um chunk específico está expandido. */
    protected isChunkOpen(citationIndex: number, chunkIndex: number): boolean {
        return this.openChunkKey() === this.chunkKey(citationIndex, chunkIndex);
    }

    /**
     * Alterna a visibilidade da lista de chunks de uma citação.
     * Não afeta qual chunk está expandido internamente.
     */
    protected toggleChunks(citation: Citation): void {
        this.openCitations.update((set) => {
            const next = new Set(set);
            next.has(citation.index) ? next.delete(citation.index) : next.add(citation.index);
            return next;
        });
    }

    /**
     * Alterna a expansão de um chunk individual.
     * Abrir um chunk fecha qualquer outro. Fechar não oculta a lista da citação.
     */
    protected toggleChunk(citationIndex: number, chunkIndex: number): void {
        const key = this.chunkKey(citationIndex, chunkIndex);
        this.openChunkKey.update((current) => (current === key ? null : key));
    }

    /** Retorna os primeiros N caracteres do conteúdo do chunk (prévia colapsada). */
    protected chunkPreview(content: string, length: number = 80): string {
        if (!content) return '';
        return content.length > length ? content.substring(0, length) + '...' : content;
    }

    /** Retorna se o chunk especificado está atualmente destacado. */
    protected isHighlighted(citationIndex: number, chunkIndex: number): boolean {
        const h = this.highlightedChunk();
        return h?.datasource === citationIndex && h?.chunk === chunkIndex;
    }

    /**
     * Expande o chunk especificado, rola a tela até ele e aplica o highlight animado.
     * Fecha qualquer outro chunk que estivesse aberto.
     */
    public scrollToChunk(datasource: number, chunk: number): void {
        // Torna a lista da citação visível
        this.openCitations.update((set) => {
            const next = new Set(set);
            next.add(datasource);
            return next;
        });

        // Expande o chunk alvo (fecha os demais)
        this.openChunkKey.set(this.chunkKey(datasource, chunk));

        setTimeout(() => {
            const elementId = `citation-${datasource}-chunk-${chunk}`;
            const el = document.getElementById(elementId);
            if (el) {
                el.scrollIntoView({behavior: 'smooth', block: 'center'});
                this.highlightedChunk.set({datasource, chunk});
                setTimeout(() => this.highlightedChunk.set(null), 1600);
            }
        }, 50);
    }

    protected handleCitationClicked(citation: Citation): void {
        this.citationClicked.emit({citation});
    }

    protected handleChunkClicked(chunk: CitationChunk, citation: Citation): void {
        this.toggleChunk(citation.index, chunk.index);
        this.citationChunkClicked.emit({citation, chunk});
    }
}
